import { useState, useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { useQuery } from 'react-query'
import { X, Plus, Trash2, Package, Search, ChevronLeft, ChevronRight, User, Calendar, ShoppingCart, FileText } from 'lucide-react'
// import { productAPI, customerAPI, employeeAPI } from '../services/api' // Removed API calls
import Button from '../common/Button'
import LoadingSpinner from '../common/LoadingSpinner'

function OrderFormSteps({ onSubmit, onCancel, initialData = null, isLoading = false }) {
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedProducts, setSelectedProducts] = useState([])
  const [productSearch, setProductSearch] = useState('')
  const [showProductModal, setShowProductModal] = useState(false)
  const [orderType, setOrderType] = useState('custom') // 'product' or 'custom'

  const { register, handleSubmit, control, watch, setValue, formState: { errors } } = useForm({
    defaultValues: initialData || {
      customerId: '',
      orderDate: new Date().toISOString().split('T')[0],
      deliveryDate: '',
      status: 'Pending',
      totalAmount: 0,
      advancePaid: 0,
      assignedEmployeeId: '',
      notes: '',
      items: [],
      discountAmount: 0,
      discountPercentage: 0,
      gstPercentage: 18
    }
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  })

  // Fetch data for dropdowns
  const { data: customersData } = useQuery('customers', () => customerAPI.getAll({ limit: 100 }))
  const { data: employeesData } = useQuery('employees', () => employeeAPI.getAll({ limit: 100 }))
  const { data: productsData, isLoading: productsLoading } = useQuery(
    ['products-for-order', { search: productSearch }],
    () => productAPI.getProductsForOrder({ search: productSearch }),
    { enabled: showProductModal }
  )

  const customers = customersData?.data?.customers || []
  const employees = employeesData?.data?.employees || []
  const products = productsData?.data || []

  // Watch form values for calculations
  const watchedItems = watch('items')
  const watchedAdvancePaid = watch('advancePaid')
  const watchedDiscountAmount = watch('discountAmount')
  const watchedDiscountPercentage = watch('discountPercentage')
  const watchedGstPercentage = watch('gstPercentage')

  // Calculate financial breakdown
  useEffect(() => {
    const subtotal = watchedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0)
    setValue('totalAmount', subtotal)
    
    // Calculate discount
    const discountAmount = watchedDiscountPercentage > 0 
      ? (subtotal * watchedDiscountPercentage) / 100 
      : (watchedDiscountAmount || 0)
    
    // Calculate amount after discount
    const amountAfterDiscount = subtotal - discountAmount
    
    // Calculate GST
    const gstAmount = (amountAfterDiscount * (watchedGstPercentage || 0)) / 100
    
    // Calculate final amount
    const finalAmount = amountAfterDiscount + gstAmount
    
    // Calculate balance
    const balance = finalAmount - (watchedAdvancePaid || 0)
    
    setValue('subtotalAmount', subtotal)
    setValue('discountAmount', discountAmount)
    setValue('gstAmount', gstAmount)
    setValue('finalAmount', finalAmount)
    setValue('balanceAmount', balance)
  }, [watchedItems, watchedAdvancePaid, watchedDiscountAmount, watchedDiscountPercentage, watchedGstPercentage, setValue])

  const handleAddProduct = (product) => {
    const existingItem = watchedItems.find(item => item.productId === product.id)
    
    if (existingItem) {
      const updatedItems = watchedItems.map(item =>
        item.productId === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      )
      setValue('items', updatedItems)
    } else {
      append({
        productId: product.id,
        itemName: product.dressName,
        price: product.totalAmount,
        quantity: 1,
        notes: ''
      })
    }
    
    setShowProductModal(false)
    setProductSearch('')
  }

  const handleRemoveItem = (index) => {
    remove(index)
  }

  const handleUpdateQuantity = (index, quantity) => {
    const updatedItems = [...watchedItems]
    updatedItems[index].quantity = parseInt(quantity) || 1
    setValue('items', updatedItems)
  }

  const handleUpdatePrice = (index, price) => {
    const updatedItems = [...watchedItems]
    updatedItems[index].price = parseFloat(price) || 0
    setValue('items', updatedItems)
  }

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const steps = [
    { id: 1, name: 'Customer & Basic Info', icon: User, description: 'Select customer and basic order details' },
    { id: 2, name: 'Order Type & Items', icon: ShoppingCart, description: 'Choose order type and add items' },
    { id: 3, name: 'Payment & Assignment', icon: FileText, description: 'Set payment details and assign employee' },
    { id: 4, name: 'Review & Confirm', icon: Calendar, description: 'Review all details and create order' }
  ]

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <User className="h-12 w-12 mx-auto text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-secondary-900">Customer & Basic Information</h3>
              <p className="text-secondary-600">Select customer and set basic order details</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Customer *</label>
                <select
                  className="input"
                  {...register('customerId', { required: 'Customer is required' })}
                >
                  <option value="">Select Customer</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.firstName} {customer.lastName} - {customer.phone}
                    </option>
                  ))}
                </select>
                {errors.customerId && (
                  <p className="form-error">{errors.customerId.message}</p>
                )}
              </div>

              <div>
                <label className="label">Assigned Employee</label>
                <select className="input" {...register('assignedEmployeeId')}>
                  <option value="">Select Employee</option>
                  {employees.map(employee => (
                    <option key={employee.id} value={employee.id}>
                      {employee.firstName} {employee.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Order Date *</label>
                <input
                  type="date"
                  className="input"
                  {...register('orderDate', { required: 'Order date is required' })}
                />
                {errors.orderDate && (
                  <p className="form-error">{errors.orderDate.message}</p>
                )}
              </div>

              <div>
                <label className="label">Delivery Date</label>
                <input
                  type="date"
                  className="input"
                  {...register('deliveryDate')}
                />
              </div>
            </div>
          </div>
        )

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <ShoppingCart className="h-12 w-12 mx-auto text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-secondary-900">Order Type & Items</h3>
              <p className="text-secondary-600">Choose how you want to add items to this order</p>
            </div>

            {/* Order Type Selection */}
            <div>
              <label className="label">Order Type</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  orderType === 'custom' 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-secondary-200 hover:border-secondary-300'
                }`}>
                  <input
                    type="radio"
                    value="custom"
                    checked={orderType === 'custom'}
                    onChange={(e) => setOrderType(e.target.value)}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium text-secondary-900">Custom Items</div>
                    <div className="text-sm text-secondary-600">Random orders, custom designs, special requests</div>
                  </div>
                </label>
                <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  orderType === 'product' 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-secondary-200 hover:border-secondary-300'
                }`}>
                  <input
                    type="radio"
                    value="product"
                    checked={orderType === 'product'}
                    onChange={(e) => setOrderType(e.target.value)}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium text-secondary-900">Product Catalog</div>
                    <div className="text-sm text-secondary-600">Select from predefined products</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Add Items Section */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-lg font-semibold text-secondary-900">Order Items</h4>
                <div className="flex flex-wrap gap-2">
                  {orderType === 'custom' ? (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        onClick={() => append({
                          productId: null,
                          itemName: '',
                          price: 0,
                          quantity: 1,
                          notes: ''
                        })}
                        variant="primary"
                        size="sm"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Custom Item
                      </Button>
                      <Button
                        type="button"
                        onClick={() => append({
                          productId: null,
                          itemName: 'Custom Blouse',
                          price: 1500,
                          quantity: 1,
                          notes: ''
                        })}
                        variant="outline"
                        size="sm"
                      >
                        Quick: Blouse
                      </Button>
                      <Button
                        type="button"
                        onClick={() => append({
                          productId: null,
                          itemName: 'Custom Kurta',
                          price: 2000,
                          quantity: 1,
                          notes: ''
                        })}
                        variant="outline"
                        size="sm"
                      >
                        Quick: Kurta
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      onClick={() => setShowProductModal(true)}
                      variant="primary"
                      size="sm"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Product
                    </Button>
                  )}
                </div>
              </div>

              {fields.length === 0 ? (
                <div className="text-center py-8 text-secondary-500">
                  <Package className="h-12 w-12 mx-auto mb-4 text-secondary-300" />
                  <p>
                    {orderType === 'custom' 
                      ? 'No custom items added yet. Click "Add Custom Item" to start.' 
                      : 'No products added yet. Click "Add Product" to start.'
                    }
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {fields.map((field, index) => (
                    <div key={field.id} className="border border-secondary-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
                        <div className="md:col-span-2">
                          <label className="label">
                            {orderType === 'custom' ? 'Item Name' : 'Product'}
                          </label>
                          {orderType === 'custom' ? (
                            <input
                              type="text"
                              className="input"
                              placeholder="Enter item name (e.g., Custom Blouse, Special Dress)"
                              {...register(`items.${index}.itemName`)}
                            />
                          ) : (
                            <input
                              type="text"
                              className="input"
                              value={field.itemName}
                              readOnly
                            />
                          )}
                        </div>

                        <div>
                          <label className="label">Quantity</label>
                          <input
                            type="number"
                            min="1"
                            className="input"
                            value={field.quantity}
                            onChange={(e) => handleUpdateQuantity(index, e.target.value)}
                          />
                        </div>

                        <div>
                          <label className="label">Price</label>
                          <input
                            type="number"
                            step="0.01"
                            className="input"
                            value={field.price}
                            onChange={(e) => handleUpdatePrice(index, e.target.value)}
                          />
                        </div>

                        <div>
                          <label className="label">Total</label>
                          <input
                            type="number"
                            step="0.01"
                            className="input"
                            value={(field.price * field.quantity).toFixed(2)}
                            readOnly
                          />
                        </div>

                        <div>
                          <Button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            variant="danger"
                            size="sm"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="mt-2">
                        <label className="label">Notes</label>
                        <input
                          type="text"
                          className="input"
                          placeholder="Additional notes for this item"
                          {...register(`items.${index}.notes`)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Order Summary */}
              {fields.length > 0 && (
                <div className="bg-secondary-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Total Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        className="input font-semibold"
                        value={watchedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2)}
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="label">Balance Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        className="input font-semibold"
                        value={watchedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0) - (watchedAdvancePaid || 0)}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <FileText className="h-12 w-12 mx-auto text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-secondary-900">Payment & Financial Details</h3>
              <p className="text-secondary-600">Set payment details, discounts, and GST</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Status</label>
                <select className="input" {...register('status')}>
                  <option value="Pending">Pending</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="label">Advance Paid</label>
                <input
                  type="number"
                  step="0.01"
                  className="input"
                  placeholder="0.00"
                  {...register('advancePaid')}
                />
              </div>
            </div>

            {/* Discount Section */}
            <div className="bg-secondary-50 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-secondary-900 mb-4">Discount</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Discount Amount (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input"
                    placeholder="0.00"
                    {...register('discountAmount')}
                  />
                </div>
                <div>
                  <label className="label">Discount Percentage (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input"
                    placeholder="0.00"
                    {...register('discountPercentage')}
                  />
                </div>
              </div>
              <p className="text-sm text-secondary-600 mt-2">
                Enter either amount or percentage. If both are provided, percentage will be used.
              </p>
            </div>

            {/* GST Section */}
            <div className="bg-secondary-50 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-secondary-900 mb-4">GST</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">GST Percentage (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input"
                    placeholder="18.00"
                    {...register('gstPercentage')}
                  />
                </div>
                <div>
                  <label className="label">GST Amount (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input"
                    value={watch('gstAmount') || 0}
                    readOnly
                  />
                </div>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="bg-primary-50 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-primary-900 mb-4">Financial Summary</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-secondary-700">Subtotal:</span>
                  <span className="font-semibold">₹{watch('totalAmount') || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary-700">Discount:</span>
                  <span className="font-semibold text-red-600">-₹{watch('discountAmount') || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary-700">Amount after discount:</span>
                  <span className="font-semibold">₹{(watch('totalAmount') || 0) - (watch('discountAmount') || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary-700">GST ({watch('gstPercentage') || 0}%):</span>
                  <span className="font-semibold text-green-600">+₹{watch('gstAmount') || 0}</span>
                </div>
                <div className="border-t pt-2">
                  <div className="flex justify-between text-lg font-bold">
                    <span className="text-primary-900">Final Amount:</span>
                    <span className="text-primary-900">₹{watch('finalAmount') || 0}</span>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary-700">Advance Paid:</span>
                  <span className="font-semibold">₹{watch('advancePaid') || 0}</span>
                </div>
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-secondary-900">Balance Amount:</span>
                  <span className="text-secondary-900">₹{watch('balanceAmount') || 0}</span>
                </div>
              </div>
            </div>

            <div>
              <label className="label">Order Notes</label>
              <textarea
                className="input"
                rows={3}
                placeholder="Any additional notes for this order"
                {...register('notes')}
              />
            </div>
          </div>
        )

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Calendar className="h-12 w-12 mx-auto text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-secondary-900">Review & Confirm</h3>
              <p className="text-secondary-600">Review all details before creating the order</p>
            </div>

            <div className="bg-secondary-50 rounded-lg p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label text-sm font-medium text-secondary-700">Customer</label>
                  <p className="text-secondary-900">
                    {customers.find(c => c.id === watch('customerId'))?.firstName} {customers.find(c => c.id === watch('customerId'))?.lastName}
                  </p>
                </div>
                <div>
                  <label className="label text-sm font-medium text-secondary-700">Order Date</label>
                  <p className="text-secondary-900">{watch('orderDate')}</p>
                </div>
                <div>
                  <label className="label text-sm font-medium text-secondary-700">Delivery Date</label>
                  <p className="text-secondary-900">{watch('deliveryDate') || 'Not set'}</p>
                </div>
                <div>
                  <label className="label text-sm font-medium text-secondary-700">Status</label>
                  <p className="text-secondary-900">{watch('status')}</p>
                </div>
              </div>

              <div>
                <label className="label text-sm font-medium text-secondary-700">Order Items</label>
                <div className="mt-2 space-y-2">
                  {watchedItems.map((item, index) => (
                    <div key={index} className="flex justify-between items-center bg-white p-3 rounded border">
                      <div>
                        <span className="font-medium">{item.itemName}</span>
                        <span className="text-sm text-secondary-600 ml-2">x{item.quantity}</span>
                      </div>
                      <span className="font-semibold">₹{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-secondary-700">Subtotal:</span>
                    <span className="font-semibold">₹{watch('totalAmount') || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-secondary-700">Discount:</span>
                    <span className="font-semibold text-red-600">-₹{watch('discountAmount') || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-secondary-700">Amount after discount:</span>
                    <span className="font-semibold">₹{(watch('totalAmount') || 0) - (watch('discountAmount') || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-secondary-700">GST ({watch('gstPercentage') || 0}%):</span>
                    <span className="font-semibold text-green-600">+₹{watch('gstAmount') || 0}</span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between items-center text-lg font-bold">
                      <span className="text-primary-900">Final Amount:</span>
                      <span className="text-primary-900">₹{watch('finalAmount') || 0}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-sm text-secondary-600">
                    <span>Advance Paid:</span>
                    <span>₹{watch('advancePaid') || 0}</span>
                  </div>
                  <div className="flex justify-between items-center text-lg font-bold">
                    <span className="text-secondary-900">Balance Amount:</span>
                    <span className="text-secondary-900">₹{watch('balanceAmount') || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
              currentStep >= step.id 
                ? 'bg-primary-600 border-primary-600 text-white' 
                : 'bg-white border-secondary-300 text-secondary-400'
            }`}>
              <step.icon className="h-5 w-5" />
            </div>
            <div className="ml-3 hidden sm:block">
              <div className={`text-sm font-medium ${
                currentStep >= step.id ? 'text-primary-600' : 'text-secondary-400'
              }`}>
                {step.name}
              </div>
              <div className="text-xs text-secondary-500">{step.description}</div>
            </div>
            {index < steps.length - 1 && (
              <div className={`hidden sm:block w-16 h-0.5 mx-4 ${
                currentStep > step.id ? 'bg-primary-600' : 'bg-secondary-300'
              }`} />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="min-h-96 max-h-[60vh] overflow-y-auto">
        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          type="button"
          onClick={prevStep}
          variant="outline"
          disabled={currentStep === 1}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        <div className="flex space-x-3">
          <Button
            type="button"
            onClick={onCancel}
            variant="outline"
          >
            Cancel
          </Button>
          
          {currentStep < 4 ? (
            <Button
              type="button"
              onClick={nextStep}
              variant="primary"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={isLoading || fields.length === 0}
              variant="primary"
              loading={isLoading}
            >
              {initialData ? 'Update Order' : 'Create Order'}
            </Button>
          )}
        </div>
      </div>

      {/* Product Selection Modal */}
      {showProductModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-start justify-center p-4 py-8">
            <div className="modal-overlay" onClick={() => setShowProductModal(false)} />
            <div className="modal-content max-w-4xl w-full max-h-[90vh] flex flex-col">
              <div className="flex justify-between items-center mb-4 flex-shrink-0">
                <h3 className="text-lg font-semibold text-secondary-900">Select Products</h3>
                <button
                  onClick={() => setShowProductModal(false)}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4 flex-shrink-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                  <input
                    type="text"
                    className="input pl-10"
                    placeholder="Search products..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {productsLoading ? (
                  <LoadingSpinner />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {products.map(product => (
                      <div
                        key={product.id}
                        className="border border-secondary-200 rounded-lg p-4 hover:border-primary-300 cursor-pointer transition-colors"
                        onClick={() => handleAddProduct(product)}
                      >
                        <div className="space-y-2">
                          <h4 className="font-medium text-secondary-900">{product.dressName}</h4>
                          <p className="text-sm text-secondary-600">{product.category}</p>
                          <p className="text-sm font-semibold text-primary-600">
                            ₹{product.totalAmount}
                          </p>
                          {product.stitchingAmount > 0 && (
                            <p className="text-xs text-secondary-500">
                              Stitching: ₹{product.stitchingAmount}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {products.length === 0 && !productsLoading && (
                  <div className="text-center py-8 text-secondary-500">
                    <Package className="h-12 w-12 mx-auto mb-4 text-secondary-300" />
                    <p>No products found. Try adjusting your search.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OrderFormSteps
